'use client'

import React, { useState, useEffect, useRef } from 'react'
import { ellipse, getBaseImageUrl, getCategoryHref, getMerchantHref } from '@/constants/hooks'
import { SearchCategories, SearchMerchant } from '@/services/dataTypes'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import Link from 'next/link'
import Image from 'next/image'
import { Search, X, TrendingUp, Store, FolderOpen, ArrowRight } from 'lucide-react'
import { keywordsAction, searchAction } from '@/app/actions'

interface Props {
    companyId: string
    mer_slug: string
    slug_type: string
    cat_slug: string
    domain: string
}

const SearchBar = ({ companyId, mer_slug, slug_type, cat_slug, domain }: Props) => {
    const searchParams = useSearchParams()
    const [search, setSearch] = useState<string>(new URLSearchParams(searchParams).get('query') || '')
    const [open, setOpen] = useState(false)
    const [focused, setFocused] = useState(false)
    const [isHovered, setIsHovered] = useState(false)
    const [tags, setTags] = useState<any[]>([])
    const [cats, setCats] = useState<SearchCategories[]>([])
    const [merchants, setMerchants] = useState<SearchMerchant[]>([])
    const [err, setErr] = useState('')
    const [mounted, setMounted] = useState(false)
    const pathName = usePathname()
    const router = useRouter()
    const wrapperRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => { setMounted(true) }, [])

    useEffect(() => {
        if (companyId) {
            keywordsAction(companyId).then(r => r.data && setTags(r.data)).catch(() => { })
        }
    }, [companyId])

    useEffect(() => {
        if (pathName === '/search') setOpen(false)
    }, [pathName])

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setOpen(false)
                setFocused(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    useEffect(() => {
        const timer = setTimeout(() => {
            if (search.trim().length >= 3) {
                searchAction(search, companyId)
                    .then(res => {
                        const m = res.data?.merchants || []
                        const c = res.data?.categories || []
                        setMerchants(m)
                        setCats(c)
                        setErr(m.length === 0 && c.length === 0 ? 'No results found.' : '')
                    })
                    .catch(() => setErr('Error searching.'))
            } else if (search.trim().length > 0) {
                setMerchants([])
                setCats([])
                setErr('Type at least 3 characters.')
            } else {
                setMerchants([])
                setCats([])
                setErr('')
            }
        }, 300)

        return () => clearTimeout(timer)
    }, [search, companyId])

    const handleSubmit = (term: string) => {
        if (!term.trim()) return
        setOpen(false)
        setFocused(false)
        router.push(`/search?query=${encodeURIComponent(term.trim())}`)
    }

    const hasDropdownContent = open && (tags.length > 0 || merchants.length > 0 || cats.length > 0 || !!err || search.trim().length > 0)

    return (
        <div ref={wrapperRef} className="relative w-full">
            <div
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                className="flex items-center h-10 sm:h-11 rounded-2xl pl-4 pr-1.5 transition-all duration-300 group cursor-text"
                style={{
                    background: focused
                        ? 'rgba(255,255,255,0.18)'
                        : isHovered
                            ? 'rgba(255,255,255,0.12)'
                            : 'rgba(255,255,255,0.08)',
                    border: focused
                        ? '1px solid rgba(245, 197, 66, 0.7)'
                        : '1px solid rgba(255, 255, 255, 0.15)',
                    boxShadow: focused
                        ? '0 8px 30px rgba(0, 0, 0, 0.35), 0 0 25px rgba(245, 197, 66, 0.2)'
                        : isHovered
                            ? '0 6px 24px rgba(0, 0, 0, 0.28), 0 0 16px rgba(245, 197, 66, 0.15)'
                            : '0 2px 8px rgba(0, 0, 0, 0.12)',
                }}
            >
                <input
                    ref={inputRef}
                    type="text"
                    placeholder="Search stores, coupons..."
                    value={search}
                    autoComplete="off"
                    onChange={e => setSearch(e.target.value)}
                    onFocus={() => {
                        setFocused(true)
                        if (pathName !== '/search') setOpen(true)
                    }}
                    onBlur={() => {
                        setTimeout(() => {
                            setFocused(false)
                            setOpen(false)
                        }, 180)
                    }}
                    onKeyDown={e => {
                        if (e.key === 'Enter') handleSubmit(search)
                    }}
                    className="flex-1 bg-transparent border-none outline-none text-xs sm:text-sm min-w-0 placeholder:text-slate-400 text-white"
                    aria-label="Search"
                />
                {search && (
                    <button
                        type="button"
                        onClick={() => {
                            setSearch('')
                            setMerchants([])
                            setCats([])
                            setErr('')
                            inputRef.current?.focus()
                        }}
                        className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-slate-400 hover:text-white transition-colors cursor-pointer mr-2"
                        aria-label="Clear search"
                    >
                        <X size={13} />
                    </button>
                )}

                <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSubmit(search)}
                    aria-label="Search"
                    className="w-7 h-7 flex-shrink-0 bg-[#F5C542] hover:brightness-110 active:scale-95 text-[#2D3E2D] rounded-full transition-all duration-300 flex items-center justify-center shadow-xs cursor-pointer group/btn"
                >
                    <Search size={12} className="group-hover/btn:scale-110 transition-transform duration-300" />
                </button>
            </div>

            {/* Dropdown Menu */}
            {mounted && hasDropdownContent && (
                <div
                    className="absolute top-[calc(100%+8px)] right-0 z-[400] w-full min-w-[300px] sm:min-w-[380px] max-w-[500px] rounded-2xl overflow-hidden bg-white border border-slate-200/80 shadow-[0_16px_40px_rgba(45,62,45,0.15)] max-h-[460px] overflow-y-auto"
                    style={{ animation: 'searchDropIn 0.15s ease-out' }}
                >
                    <style dangerouslySetInnerHTML={{
                        __html: `@keyframes searchDropIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }`
                    }} />

                    {/* Trending / Popular tags when input is empty */}
                    {tags.length > 0 && !search.trim() && (
                        <div className="p-4 border-b border-slate-100">
                            <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[#5C7A5C] mb-2.5">
                                <TrendingUp size={11} className="text-[#5C7A5C]" /> Popular Searches
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                                {tags.slice(0, 10).map((tag, i) => (
                                    <button
                                        key={i}
                                        type="button"
                                        onClick={() => handleSubmit(tag)}
                                        className="text-xs font-semibold px-3 py-1.5 rounded-full bg-[#E8F0E8] text-[#2D3E2D] hover:bg-[#2D3E2D] hover:text-[#F5C542] transition-all duration-150 cursor-pointer border border-[#5C7A5C]/20"
                                    >
                                        {tag}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Error message */}
                    {err && (
                        <div className="p-4 text-xs font-medium text-slate-500 text-center">
                            {err}
                        </div>
                    )}

                    {/* Stores Results */}
                    {merchants.length > 0 && (
                        <div className="p-3 border-b border-slate-100">
                            <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[#5C7A5C] px-2 mb-2">
                                <Store size={11} className="text-[#5C7A5C]" /> Stores
                            </p>
                            <div className="flex flex-col gap-1">
                                {merchants.slice(0, 5).map((m, i) => (
                                    <Link
                                        key={i}
                                        href={getMerchantHref(m, mer_slug, slug_type)}
                                        onClick={() => setOpen(false)}
                                        className="flex items-center gap-3 px-3 py-2 rounded-xl text-xs sm:text-sm font-semibold text-[#2D3E2D] hover:bg-[#E8F0E8] transition-colors"
                                    >
                                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 p-1 bg-slate-50 border border-slate-200/60 overflow-hidden">
                                            <Image
                                                src={getBaseImageUrl(domain, m.merchant_logo, '')}
                                                alt={m.merchant_name}
                                                width={24}
                                                height={24}
                                                className="object-contain max-h-6 w-auto h-auto"
                                            />
                                        </div>
                                        <span className="truncate">{ellipse(m.merchant_name, 35)}</span>
                                        <ArrowRight size={12} className="ml-auto text-slate-400 opacity-0 group-hover:opacity-100" />
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Categories Results */}
                    {cats.length > 0 && (
                        <div className="p-3 border-b border-slate-100">
                            <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[#5C7A5C] px-2 mb-2">
                                <FolderOpen size={11} className="text-[#5C7A5C]" /> Categories
                            </p>
                            <div className="flex flex-wrap gap-1.5 px-2">
                                {cats.slice(0, 6).map((c, i) => (
                                    <Link
                                        key={i}
                                        href={getCategoryHref(c, cat_slug, slug_type)}
                                        onClick={() => setOpen(false)}
                                        className="text-xs font-semibold px-3 py-1.5 rounded-full bg-[#E8F0E8] text-[#2D3E2D] hover:bg-[#2D3E2D] hover:text-[#F5C542] transition-colors border border-[#5C7A5C]/20"
                                    >
                                        {c.name}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Direct Search query footer */}
                    {search.trim().length > 0 && (
                        <button
                            type="button"
                            onClick={() => handleSubmit(search)}
                            className="w-full flex items-center justify-between p-3.5 text-xs font-bold text-[#2D3E2D] bg-slate-50 hover:bg-[#E8F0E8] transition-colors cursor-pointer text-left"
                        >
                            <span>Search all deals for &quot;<span className="text-[#5C7A5C]">{search.trim()}</span>&quot;</span>
                            <ArrowRight size={13} className="text-[#5C7A5C]" />
                        </button>
                    )}
                </div>
            )}
        </div>
    )
}

export default SearchBar
